import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  OnboardingAnswerValue,
  OnboardingCreateAnswerResponse,
  OnboardingQuestionApiDto,
} from '../models/login-v2-api.models';
import {
  OnboardingAnalyticsStep,
  OnboardingQuestionUiType,
  OnboardingQuestionView,
  OnboardingScreen,
} from '../models/onboarding.models';
import { LoginV2ApiService } from './login-v2-api.service';

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly api = inject(LoginV2ApiService);

  getQuestions(): Observable<OnboardingQuestionView[]> {
    return this.api.getOnboardingQuestions().pipe(
      map((res) => {
        if (!res.success || !res.items) {
          if (res.message) {
            this.api.toast(res.message, 'error');
          }
          return [];
        }
        return res.items.map((item) => this.fromApiDto(item));
      }),
    );
  }

  saveAnswer(
    questionId: number,
    answer: Exclude<OnboardingAnswerValue, null>,
  ): Observable<boolean> {
    return this.api
      .createOnboardingAnswer(questionId, { answer })
      .pipe(
        map((res: OnboardingCreateAnswerResponse) => {
          if (!res.success && res.message) {
            this.api.toast(res.message, 'error');
          }
          return Boolean(res.success);
        }),
      );
  }

  toAnalyticsStep(screen: OnboardingScreen): OnboardingAnalyticsStep | null {
    if (screen === 'intro') {
      return 'intro';
    }
    if (screen === 0) {
      return 'question_1';
    }
    if (screen === 1) {
      return 'question_2';
    }
    return null;
  }

  hasAnswer(
    question: OnboardingQuestionView,
    value: unknown,
  ): boolean {
    if (question.uiType === 'radio') {
      return typeof value === 'string' && value.length > 0;
    }
    return Array.isArray(value) && value.length > 0;
  }

  private fromApiDto(dto: OnboardingQuestionApiDto): OnboardingQuestionView {
    const uiType = this.resolveUiType(dto);
    const options = (dto.answers_array ?? []).map((label) => ({
      id: label,
      label,
    }));

    return {
      id: dto.id,
      name: dto.name,
      required: dto.required,
      type: dto.type,
      uiType,
      options,
      allowSkip: !dto.required,
      answered: dto.answered,
      answer: dto.answer,
    };
  }

  private resolveUiType(
    dto: OnboardingQuestionApiDto,
  ): OnboardingQuestionUiType {
    if (dto.type === 'option' && dto.option_multiselect) {
      return 'checkbox';
    }
    return 'radio';
  }
}
